import React, { FC, useEffect, useMemo } from 'react';
import {
  Network,
  RiskCurrentResponse,
  useExplorerAddressMainQuery,
  useMeQuery,
} from '@apolloGenerated';
import { Link } from 'react-justanother-router';
import { Typography } from '@rubin-dev/goblin';
import { Blur, NetworkIconEnum } from '@shared/ui';
import { useTranslation } from 'react-i18next';
import cls from './styles.module.scss';
import { RiskDot } from '@component/NewRisk/ui/RiskDot/RiskDot';
import { RouterName } from '../../../../router';
import { getRiskColor } from '@helpers/address';
import { AddressBalanceCard } from '@shared/ui/AddressBalanceCard';
import { AddressTransactionsCard } from '@graph-sidebar-module/entities/Address/ui/AddressTransactionsCard/AddressTransactionsCard';
import { setFormatDate } from '@utils/timestamp';
import { useCurrencyListStore } from '@graph/libs';
import cx from 'classnames';

export type AddressMainInfoProps = {
  address: string;
  network: Network;
  hideRisk?: boolean;
  hideCluster?: boolean;
  hideCategory?: boolean;
  hideOwner?: boolean;
  hideTransactions?: boolean;
  hideBalance?: boolean;
  hideActivity?: boolean;
  categoryDict?: RiskCurrentResponse;
};
export const AddressMainInfo: FC<AddressMainInfoProps> = ({
  address,
  network,
  hideRisk,
  hideCluster,
  hideCategory,
  hideOwner,
  hideTransactions,
  hideBalance,
  hideActivity,
  categoryDict,
}) => {
  const { t } = useTranslation();
  const { currentCurrency, changeCurrencyCurrent } = useCurrencyListStore();

  useEffect(() => {
    if (network === Network.Btc) {
      changeCurrencyCurrent(NetworkIconEnum.Btc);
      return;
    }

    changeCurrencyCurrent(NetworkIconEnum.Usdt);
  }, [network]);

  const { data: user } = useMeQuery({ fetchPolicy: 'network-only' });

  const variables = {
    network,
    address,
  };

  const { data } = useExplorerAddressMainQuery({
    variables,
  });

  const reported = data?.explorerRisk.risk?.reported?.name || undefined;

  const owner = data?.explorerAddress?.owner;

  const cluster = data?.explorerClusterForAddress?.cluster;

  const findCategory = useMemo(
    () =>
      Object.values(categoryDict?.params || {})?.find(
        (category) => category.number === cluster?.riskCategory,
      ),
    [categoryDict, cluster],
  );

  const categoryName = useMemo(() => {
    return findCategory ? findCategory.name : '';
  }, [findCategory, cluster]);

  const risk = Number(data?.explorerRisk?.risk?.risk);

  const activity = data?.explorerAddressActivity?.activity;

  const expireDate = useMemo(
    () => user?.me.billing.analyzerSubscriptionExpireDate,
    [user?.me.billing.analyzerSubscriptionExpireDate],
  );

  const setLink = (wid?: string | undefined, owner?: string | null): JSX.Element => {
    if (!wid) return <></>;
    return (
      <Link
        to={RouterName.ExplorerWallet}
        params={{ wid: String(wid), network }}
        target={'_blank'}
      >
        <Typography variant="body-16" className={cls.text} color={'primary-1'}>
          {!owner ? 'Entity' : owner}
        </Typography>
      </Link>
    );
  };

  return (
    <div className={cls.container}>
      <div className={cls.item}>
        <div className={cls.wrapper}>
          {((!hideRisk && !isNaN(risk)) || !expireDate) && (
            <div className={cls.group}>
              <Typography
                variant="body-16"
                className={cls.text}
                color="on-surface-primary-1"
              >
                {t('strings.riskscore')}
              </Typography>
              {expireDate ? (
                <Typography tag="div" variant="head-16" className={cls.risk}>
                  <RiskDot risk={Number(risk)} size={12} />
                  {risk}%
                </Typography>
              ) : (
                <Blur to={RouterName.TariffAnalyzer}>
                  <RiskDot risk={Number(risk)} size={12} />
                  000%
                </Blur>
              )}
            </div>
          )}

          {cluster && (!hideCluster || expireDate) && (
            <div className={cls.group}>
              <Typography
                variant="body-16"
                className={cls.text}
                color="on-surface-primary-1"
              >
                {t('mention.cluster')}
              </Typography>
              <Typography tag={'div'} variant="body-16" className={cls.risk}>
                {expireDate ? (
                  setLink(cluster?.id, cluster?.owner)
                ) : (
                  <Blur to={RouterName.TariffAnalyzer}>
                    <Typography variant="body-16" className={cls.text} color="primary-1">
                      Cluster
                    </Typography>
                  </Blur>
                )}
              </Typography>
            </div>
          )}

          {(!!(!hideCategory && categoryName) || !expireDate || !!reported) && (
            <div className={cls.group}>
              <Typography
                variant="body-16"
                className={cls.text}
                color="on-surface-primary-1"
              >
                {t(reported ? 'strings.reported' : 'links.category')}
              </Typography>
              {expireDate ? (
                <Typography
                  variant="body-16"
                  className={cls.text}
                  style={{ color: getRiskColor(risk) }}
                >
                  {reported || categoryName}
                </Typography>
              ) : (
                <Blur to={RouterName.TariffAnalyzer}>
                  <Typography variant="body-16" className={cls.text} color="error-2">
                    Category Name
                  </Typography>
                </Blur>
              )}
            </div>
          )}
          {(!!(!hideOwner && owner) || !expireDate) && (
            <div className={cls.group}>
              <Typography
                variant="body-16"
                className={cls.text}
                color="on-surface-primary-1"
              >
                {t('strings.owner')}
              </Typography>
              {expireDate ? (
                <Typography variant="body-16" className={cls.text} color="error-2">
                  {owner}
                </Typography>
              ) : (
                <Blur to={RouterName.TariffAnalyzer}>
                  <Typography variant="head-16" color="error-2">
                    Owner
                  </Typography>
                </Blur>
              )}
            </div>
          )}
        </div>
      </div>

      {!!(!hideBalance && data?.explorerBalance) && (
        <div className={cls.item}>
          <AddressBalanceCard
            balance={data?.explorerBalance || null}
            network={network}
            isSimpleView
            currentCurrency={currentCurrency}
          />
        </div>
      )}

      {!!(!hideTransactions && data?.explorerAddressTransactionStats) && (
        <div className={cls.item}>
          <AddressTransactionsCard address={address} network={network} isSimpleView />
        </div>
      )}

      {!!(!hideActivity && activity) && (
        <div className={cls.item}>
          <Typography
            variant="body-16"
            className={cx(cls.text, cls.activity)}
            color="on-surface-primary-1"
          >
            {t('titles.activity')}
          </Typography>
          <Typography
            tag={'div'}
            className={cls.text}
            variant="body-16"
            color="on-surface-primary-1"
          >
            {setFormatDate(activity.first)}-{setFormatDate(activity.last)}
          </Typography>
        </div>
      )}
    </div>
  );
};
